import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SignaturePad from '../../components/admin/SignaturePad';
import {
  EXTERIOR_CODES, INTERIOR_CODES, FUEL_LABELS,
  todayISO, formatErr, notifyErr, emptyForm, inputCls,
} from '../../components/admin/inspection/constants';
import { Section, Field } from '../../components/admin/inspection/SectionField';
import DamageGrid from '../../components/admin/inspection/DamageGrid';
import MediaPanel from '../../components/admin/inspection/MediaPanel';
import CompletionChecklist from '../../components/admin/inspection/CompletionChecklist';
import {
  Save, Loader2, FileText, Download, Share2, Receipt, X as XIcon,
} from 'lucide-react';
import { API } from '../../lib/api';
import { renderMessage, waUrl } from '../../lib/waShare';
import { useApiFeedback } from '../../lib/useApiFeedback';

const InspectionForm = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const { error: err, showError, showSuccess, clearError, bannerRef: errBannerRef } = useApiFeedback();
  const [savedAt, setSavedAt] = useState(null);
  const [media, setMedia] = useState([]);
  // Billing customer link + invoice (set when convert-to-invoice has billed it)
  const [customers, setCustomers] = useState([]);
  const [linkedInvoice, setLinkedInvoice] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/billing/customers`, { headers: authHeader() });
      setCustomers(data || []);
    } catch (e) {
      console.warn('[InspectionForm] customer list fetch failed:', e?.message || e);
    }
  }, [authHeader]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchLinkedInvoice = useCallback(async (invId) => {
    if (!invId) { setLinkedInvoice(null); return; }
    try {
      const { data } = await axios.get(`${API}/billing/invoices/${invId}`, { headers: authHeader() });
      setLinkedInvoice(data);
    } catch {
      setLinkedInvoice(null);
    }
  }, [authHeader]);

  const fetchOne = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inspections/${id}`, { headers: authHeader(), timeout: 30000 });
      const f = { ...emptyForm(), ...data };
      // ensure objects are not null
      f.exterior_issues = data.exterior_issues || [];
      f.interior_issues = data.interior_issues || [];
      f.exterior_notes = data.exterior_notes || {};
      f.interior_notes = data.interior_notes || {};
      f.completion_checklist = data.completion_checklist || {};
      f.completion_notes = data.completion_notes || '';
      f.completion_date = (data.completion_date || '').slice(0, 10) || '';
      f.checked_by_name = data.checked_by_name || '';
      f.checked_by_signature = data.checked_by_signature || '';
      f.approved_by_name = data.approved_by_name || '';
      f.approved_by_signature = data.approved_by_signature || '';
      f.kms_run = data.kms_run ?? '';
      f.estimate = data.estimate ?? '';
      f.battery_charge_level = data.battery_charge_level ?? '';
      f.fuel_level = data.fuel_level ?? 4;
      f.executive_signature = data.executive_signature || '';
      f.inspection_date = (data.inspection_date || '').slice(0, 10) || todayISO();
      f.customer_id = data.customer_id || '';
      f.billed_invoice_id = data.billed_invoice_id || '';
      setForm(f);
      setMedia(data.media || []);
      fetchLinkedInvoice(data.billed_invoice_id || '');
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  }, [id, isNew, authHeader, fetchLinkedInvoice, showError]);

  useEffect(() => { fetchOne(); }, [fetchOne]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // When a billing customer is selected, copy their details into the form so
  // the inspection PDF + WhatsApp share use the same name/phone/email/address.
  const linkCustomer = (cid) => {
    if (!cid) {
      setForm((s) => ({ ...s, customer_id: '' }));
      return;
    }
    const c = customers.find((x) => x.id === cid);
    if (!c) return;
    const displayName = [c.prefix, c.name].filter(Boolean).join(' ');
    setForm((s) => ({
      ...s,
      customer_id: cid,
      customer_name: displayName || s.customer_name,
      contact_number: c.phone || s.contact_number,
      email: c.email || s.email,
      address: c.address || s.address,
    }));
  };

  const toggleCode = (kind, code) => {
    const key = kind === 'ext' ? 'exterior_issues' : 'interior_issues';
    setForm((s) => {
      const list = s[key];
      const next = list.includes(code) ? list.filter((c) => c !== code) : [...list, code];
      return { ...s, [key]: next };
    });
  };

  const setNote = (kind, code, note) => {
    const key = kind === 'ext' ? 'exterior_notes' : 'interior_notes';
    setForm((s) => ({ ...s, [key]: { ...s[key], [code]: note } }));
  };

  const buildPayload = () => {
    const p = { ...form };
    // Coerce numerics
    p.kms_run = p.kms_run === '' ? null : Number(p.kms_run);
    p.estimate = p.estimate === '' ? null : Number(p.estimate);
    p.battery_charge_level = p.battery_charge_level === '' ? null : Number(p.battery_charge_level);
    p.fuel_level = p.fuel_level === '' ? null : Number(p.fuel_level);
    return p;
  };

  const save = async ({ markCompleted = false } = {}) => {
    clearError();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (markCompleted) payload.status = 'completed';
      let saved;
      const cfg = { headers: authHeader(), timeout: 60000, maxContentLength: 10 * 1024 * 1024, maxBodyLength: 10 * 1024 * 1024 };
      if (isNew) {
        const { data } = await axios.post(`${API}/inspections`, payload, cfg);
        saved = data;
        navigate(`/admin/inspections/${data.id}`, { replace: true });
      } else {
        const { data } = await axios.put(`${API}/inspections/${id}`, payload, cfg);
        saved = data;
        setForm((s) => ({ ...s, status: saved.status }));
      }
      setSavedAt(new Date());
      showSuccess(markCompleted ? 'Inspection marked complete.' : 'Inspection saved.');
    } catch (e) {
      showError(e);
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (isNew) { showError('Please save the form first.'); return; }
    clearError();
    try {
      const res = await axios.get(`${API}/inspections/${id}/pdf`, {
        headers: authHeader(), responseType: 'blob', timeout: 60000,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Inspection_${(form.customer_name || 'form').replace(/\s+/g, '_')}_${id.slice(0, 8)}.pdf`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (e) {
      showError(e);
    }
  };

  // viewPdf replaced by a direct <a href> in the JSX — keeps the click a
  // trusted user gesture and dodges popup-blocker false positives.

  const shareWhatsApp = async () => {
    if (isNew) { alert('Please save the form first.'); return; }
    try {
      // 1) Mint a token-protected public share link (PDF + photo URLs)
      const { data: share } = await axios.post(
        `${API}/inspections/${id}/share`, {}, { headers: authHeader(), timeout: 30000 }
      );
      // 2) Build a WhatsApp message that contains the links — wa.me cannot
      //    attach files, but customers tap the link in-chat to download.
      const photos = (share.photo_urls || []).length
        ? ['📷 Photos:', ...share.photo_urls.map((p, i) => `  ${i + 1}. ${p.name || `Photo ${i + 1}`}: ${p.url}`)].join('\n') : '';
      const message = renderMessage('inspection', {
        customer: form.customer_name || 'Sir/Madam',
        vehicle: [form.make, form.model].filter(Boolean).join(' ') || 'vehicle',
        estimate: form.estimate || '—', delivery: form.estimated_delivery_time || '—',
        pdf_link: share.pdf_url || '', photos,
      });
      const phoneRaw = (form.contact_number || '').replace(/\D/g, '');
      const phone = phoneRaw.length >= 10 ? phoneRaw : '';
      const url = waUrl(phone, message);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      notifyErr(e);
    }
  };

  const headerTitle = isNew ? 'New Inspection' : (form.customer_name || 'Inspection');

  // Spin up a draft estimate pre-populated from this inspection — saves the
  // user from re-typing the same details on the workshop floor. The new
  // estimate carries: the linked customer, today's date, a line item with
  // the inspection's `estimate` amount as the rate (and the inspection's
  // remarks/serial as the description). Navigates to the new estimate
  // after creation so the user can review/save/convert as usual.
  const createEstimateFromInspection = async () => {
    if (isNew) { alert('Save the inspection first.'); return; }
    if (!form.customer_id) {
      alert('Link a billing customer above first — estimates need a customer profile.');
      return;
    }
    const estAmount = Number(form.estimate || 0);
    if (estAmount <= 0) {
      const ok = window.confirm(
        'This inspection has no estimate amount yet. Create a draft estimate with zero-rate items?',
      );
      if (!ok) return;
    }
    try {
      const veh = [form.make, form.model, form.vehicle_year].filter(Boolean).join(' ') || 'Vehicle';
      const desc_lines = [];
      if (form.remarks) desc_lines.push(form.remarks);
      if (form.customer_observations) desc_lines.push(`Customer notes: ${form.customer_observations}`);
      const payload = {
        customer_id: form.customer_id,
        subject: `Workshop services — ${veh}`,
        items: [{
          name: `Workshop services — ${veh}`,
          description: desc_lines.join('\n'),
          qty: 1,
          rate: estAmount,
        }],
        status: 'draft',
      };
      const { data } = await axios.post(`${API}/billing/estimates`, payload, { headers: authHeader() });
      navigate(`/admin/billing/estimates/${data.id}`);
    } catch (e) {
      notifyErr(e);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Action bar — sticky on top, contains the heavy actions (Save / View PDF / Download / WhatsApp).
          On lg+ it lives right under the page; on mobile it sits below the AdminLayout's top bar. */}
      <div className="sticky top-0 lg:top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-lg font-semibold tracking-tight truncate" data-testid="form-title">{headerTitle}</h1>
            <p className="text-xs text-slate-500 truncate">
              {isNew ? 'Create a new inspection record' : `Status: ${form.status || 'draft'}${savedAt ? ` · saved ${savedAt.toLocaleTimeString()}` : ''}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {!isNew && (
              <>
                <a
                  href={`${API}/inspections/${id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-2.5 sm:px-3 py-2 rounded-lg transition"
                  data-testid="view-pdf-btn"
                  title="Open PDF in a new tab"
                >
                  <FileText size={14} /> <span className="hidden sm:inline">View </span>PDF
                </a>
                {!form.billed_invoice_id && (
                  <button
                    type="button"
                    onClick={createEstimateFromInspection}
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-violet-100 hover:bg-violet-200 border border-violet-300 text-violet-700 px-2.5 sm:px-3 py-2 rounded-lg transition"
                    data-testid="create-estimate-btn"
                    title="Generate a draft estimate from this inspection"
                  >
                    <Receipt size={14} /> <span className="hidden sm:inline">Create </span>Estimate
                  </button>
                )}
                <button
                  type="button"
                  onClick={downloadPdf}
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-2.5 sm:px-3 py-2 rounded-lg transition"
                  data-testid="download-pdf-btn"
                  title="Download PDF"
                >
                  <Download size={14} /> <span className="hidden sm:inline">Download</span>
                </button>
                <button
                  type="button"
                  onClick={shareWhatsApp}
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-700 px-2.5 sm:px-3 py-2 rounded-lg transition"
                  data-testid="share-whatsapp-btn"
                  title="Generates a public link and opens WhatsApp with a pre-filled message"
                >
                  <Share2 size={14} /> <span className="hidden sm:inline">WhatsApp</span>
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => save()}
              disabled={saving || !form.customer_name?.trim()}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-teal-500 hover:bg-teal-400 disabled:bg-teal-700 disabled:opacity-60 text-slate-950 font-semibold px-3 sm:px-4 py-2 rounded-lg transition"
              data-testid="save-btn"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {loading && (
          <div className="text-center py-16 text-slate-400">
            <Loader2 className="animate-spin inline mr-2" size={18} /> Loading…
          </div>
        )}
        {err && (
          <div
            ref={errBannerRef}
            className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
            data-testid="form-error"
            role="alert"
            aria-live="assertive"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="flex-1">{err}</span>
              <button type="button" onClick={clearError} className="text-red-300/70 hover:text-red-200 shrink-0" aria-label="Dismiss error">
                <XIcon size={14} />
              </button>
            </div>
          </div>
        )}

        {!loading && (
          <>
            {/* Header / SL */}
            <Section title="Form Details" testid="section-meta">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Sl. No.">
                  <input
                    value={form.serial_no}
                    onChange={(e) => set('serial_no', e.target.value)}
                    className={inputCls}
                    placeholder={isNew ? 'Auto-generated on save' : ''}
                    readOnly={!isNew}
                    data-testid="serial-no"
                  />
                </Field>
                <Field label="Date" required>
                  <input type="date" value={form.inspection_date || ''} onChange={(e) => set('inspection_date', e.target.value)} className={inputCls} data-testid="inspection-date" />
                </Field>
                <Field label="Status">
                  <select value={form.status} onChange={(e) => set('status', e.target.value)} className={inputCls} data-testid="status-select">
                    <option value="draft">Draft</option>
                    <option value="completed">Completed</option>
                  </select>
                </Field>
              </div>
            </Section>

            {/* Customer */}
            <Section title="Customer Details" testid="section-customer">
              <div className="space-y-4">
                <Field label="Linked customer (optional)">
                  <div className="flex items-center gap-2">
                    <select
                      value={form.customer_id || ''}
                      onChange={(e) => linkCustomer(e.target.value)}
                      className={inputCls}
                      data-testid="link-customer-select"
                    >
                      <option value="">— Not linked —</option>
                      {customers.map((c) => {
                        const display = [c.prefix, c.name].filter(Boolean).join(' ');
                        return <option key={c.id} value={c.id}>{display}{c.phone ? ` · ${c.phone}` : ''}</option>;
                      })}
                    </select>
                    <button
                      type="button"
                      onClick={() => window.open('/admin/billing/customers', '_blank')}
                      className="px-3 py-2 text-xs whitespace-nowrap bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg text-slate-700"
                      title="Add a new customer profile"
                      data-testid="add-customer-link-btn"
                    >
                      + New
                    </button>
                  </div>
                  {form.customer_id && (
                    <p className="mt-1 text-xs text-emerald-700">
                      Linked — customer details auto-filled below. Editing fields below won&apos;t update the profile.
                    </p>
                  )}
                </Field>

                {linkedInvoice && (
                  <div
                    className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 text-sm flex items-center justify-between gap-3"
                    data-testid="linked-invoice-banner"
                  >
                    <div className="min-w-0">
                      <span className="text-violet-800 font-medium">Billed on </span>
                      <span className="font-mono text-violet-900">{linkedInvoice.number}</span>
                      <span className="text-violet-700"> · ₹ {(linkedInvoice.total_due || 0).toLocaleString('en-IN')}</span>
                      <span className="ml-2 text-xs uppercase tracking-wider text-violet-600">{linkedInvoice.status}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/billing/invoices/${linkedInvoice.id}`)}
                      className="text-xs text-violet-700 hover:text-violet-900 font-medium whitespace-nowrap"
                      data-testid="open-linked-invoice-btn"
                    >
                      Open invoice →
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Customer name" required>
                    <input value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} className={inputCls} data-testid="customer-name" />
                  </Field>
                  <Field label="Contact number">
                    <input value={form.contact_number} onChange={(e) => set('contact_number', e.target.value)} placeholder="+91 9876543210" className={inputCls} data-testid="contact-number" />
                  </Field>
                  <Field label="Email">
                    <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} data-testid="email" />
                  </Field>
                  <Field label="Address">
                    <input value={form.address} onChange={(e) => set('address', e.target.value)} className={inputCls} data-testid="address" />
                  </Field>
                </div>
              </div>
            </Section>

            {/* Vehicle */}
            <Section title="Vehicle Details" testid="section-vehicle">
              {(() => {
                const linkedCustomer = form.customer_id
                  ? customers.find((x) => x.id === form.customer_id)
                  : null;
                const customerVehicles = (linkedCustomer?.vehicles || []).filter(
                  (v) => v.make || v.model || v.registration_number,
                );
                if (!linkedCustomer) return null;
                return (
                  <div className="mb-4">
                    <Field label="Select vehicle from customer profile">
                      <div className="flex flex-wrap gap-2 items-center">
                        {customerVehicles.length > 0 ? (
                          <select
                            className={`${inputCls} max-w-md`}
                            data-testid="customer-vehicle-select"
                            value=""
                            onChange={(e) => {
                              const v = customerVehicles.find((x) => x.id === e.target.value);
                              if (!v) return;
                              setForm((s) => ({
                                ...s,
                                make: v.make || s.make,
                                model: v.model || s.model,
                                registration_number: v.registration_number || s.registration_number,
                              }));
                            }}
                          >
                            <option value="">
                              {customerVehicles.length === 1
                                ? '1 vehicle on profile — pick to autofill'
                                : `${customerVehicles.length} vehicles on profile — pick to autofill`}
                            </option>
                            {customerVehicles.map((v) => {
                              const label = [v.make, v.model].filter(Boolean).join(' ') || '(unnamed)';
                              const reg = v.registration_number ? ` · ${v.registration_number}` : '';
                              return (
                                <option key={v.id} value={v.id}>{label}{reg}</option>
                              );
                            })}
                          </select>
                        ) : (
                          <span className="text-xs text-slate-500 italic" data-testid="no-customer-vehicles">
                            No vehicles on this customer&apos;s profile yet.
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => window.open(`/admin/billing/customers?focus=${form.customer_id}`, '_blank')}
                          className="text-xs text-teal-700 underline hover:text-teal-800"
                          data-testid="add-vehicle-to-customer"
                        >
                          + Add / edit vehicles for this customer
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Picking a vehicle fills the Make &amp; Model below. You can still hand-edit if needed.
                      </p>
                    </Field>
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Field label="Year">
                  <input value={form.vehicle_year} onChange={(e) => set('vehicle_year', e.target.value)} className={inputCls} data-testid="vehicle-year" />
                </Field>
                <Field label="Make">
                  <input value={form.make} onChange={(e) => set('make', e.target.value)} placeholder="Honda, BMW, Mahindra…" className={inputCls} data-testid="make" />
                </Field>
                <Field label="Model">
                  <input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="City, X5, XUV700…" className={inputCls} data-testid="model" />
                </Field>
                <Field label="Registration number">
                  <input value={form.registration_number || ''} onChange={(e) => set('registration_number', e.target.value.toUpperCase())} placeholder="KA01AB1234" className={`${inputCls} font-mono tracking-wider uppercase`} data-testid="registration-number" />
                </Field>
                <Field label="Kms run">
                  <input type="number" min="0" value={form.kms_run} onChange={(e) => set('kms_run', e.target.value)} className={inputCls} data-testid="kms-run" />
                </Field>
                <Field label={`Fuel level (${FUEL_LABELS[form.fuel_level] || '—'})`}>
                  <input type="range" min="0" max="8" step="1" value={form.fuel_level || 0} onChange={(e) => set('fuel_level', Number(e.target.value))} className="w-full accent-teal-500 cursor-pointer" data-testid="fuel-level" />
                  <div className="flex justify-between text-[10px] text-slate-500 px-0.5 mt-1">
                    {FUEL_LABELS.map((l) => <span key={l}>{l}</span>)}
                  </div>
                </Field>
                <Field label="Battery charge level (%)">
                  <input type="number" min="0" max="100" value={form.battery_charge_level} onChange={(e) => set('battery_charge_level', e.target.value)} className={inputCls} data-testid="battery-level" placeholder="0–100" />
                </Field>
              </div>
            </Section>

            {/* Logistics */}
            <Section title="Pickup / Service / Delivery" testid="section-logistics">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Pickup at">
                  <input value={form.pickup_location} onChange={(e) => set('pickup_location', e.target.value)} className={inputCls} data-testid="pickup-location" />
                </Field>
                <Field label="Service at">
                  <input value={form.service_location} onChange={(e) => set('service_location', e.target.value)} className={inputCls} data-testid="service-location" />
                </Field>
                <Field label="Delivery at">
                  <input value={form.drop_location} onChange={(e) => set('drop_location', e.target.value)} className={inputCls} data-testid="drop-location" />
                </Field>
                <Field label="Estimated delivery time">
                  <input value={form.estimated_delivery_time} onChange={(e) => set('estimated_delivery_time', e.target.value)} placeholder="e.g. 6 hours, 5 Feb 6:00 PM" className={inputCls} data-testid="eta" />
                </Field>
                <Field label="Receiver name">
                  <input value={form.receiver_name} onChange={(e) => set('receiver_name', e.target.value)} className={inputCls} data-testid="receiver-name" />
                </Field>
              </div>
            </Section>

            {/* Exterior */}
            <Section title="Exterior Condition" testid="section-exterior">
              <DamageGrid
                codes={EXTERIOR_CODES}
                selected={form.exterior_issues}
                notes={form.exterior_notes}
                onToggle={(c) => toggleCode('ext', c)}
                onNote={(c, n) => setNote('ext', c, n)}
                testid="ext"
              />
            </Section>

            {/* Interior */}
            <Section title="Interior Condition" testid="section-interior">
              <DamageGrid
                codes={INTERIOR_CODES}
                selected={form.interior_issues}
                notes={form.interior_notes}
                onToggle={(c) => toggleCode('int', c)}
                onNote={(c, n) => setNote('int', c, n)}
                testid="int"
              />
            </Section>

            {/* Remarks / Estimate */}
            <Section title="Remarks &amp; Estimate" testid="section-remarks">
              <div className="space-y-4">
                <Field label="Remarks">
                  <textarea rows={3} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} data-testid="remarks" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Estimate (₹)">
                    <input type="number" min="0" step="0.01" value={form.estimate} onChange={(e) => set('estimate', e.target.value)} className={inputCls} data-testid="estimate" />
                  </Field>
                  <Field label="Executive Name / ID">
                    <input value={form.executive_name_id} onChange={(e) => set('executive_name_id', e.target.value)} className={inputCls} data-testid="executive" />
                  </Field>
                </div>
                <Field label="Customer requests / observations">
                  <textarea rows={3} value={form.customer_observations} onChange={(e) => set('customer_observations', e.target.value)} className={inputCls} data-testid="customer-observations" />
                </Field>
              </div>
            </Section>

            {/* Photos / videos */}
            {!isNew && (
              <Section title="Photos &amp; Videos" testid="section-media">
                <MediaPanel
                  inspectionId={id}
                  media={media}
                  authHeader={authHeader}
                  onChanged={fetchOne}
                  stage="inspection"
                />
              </Section>
            )}
            {isNew && (
              <Section title="Photos &amp; Videos" testid="section-media-locked">
                <p className="text-sm text-slate-400">Save the inspection first to attach photos and videos.</p>
              </Section>
            )}

            {/* Work Completion / Handover Checklist */}
            <Section title="Work Completion Checklist" testid="section-completion">
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Completion date">
                    <input
                      type="date"
                      value={form.completion_date || ''}
                      onChange={(e) => set('completion_date', e.target.value)}
                      className={inputCls}
                      data-testid="completion-date"
                    />
                  </Field>
                </div>
                <CompletionChecklist
                  checklist={form.completion_checklist}
                  onChange={(next) => set('completion_checklist', next)}
                />
                <Field label="Notes / Observations">
                  <textarea
                    rows={3}
                    value={form.completion_notes}
                    onChange={(e) => set('completion_notes', e.target.value)}
                    className={inputCls}
                    placeholder="Anything the technician wants to record before handover…"
                    data-testid="completion-notes"
                  />
                </Field>
              </div>
            </Section>

            {/* Completion photos — separate gallery from incoming media */}
            {!isNew && (
              <Section title="Completion Photos (post-work)" testid="section-completion-media">
                <MediaPanel
                  inspectionId={id}
                  media={media}
                  authHeader={authHeader}
                  onChanged={fetchOne}
                  stage="completion"
                  uploadLabel="Add completion photos / videos"
                />
              </Section>
            )}
            {isNew && (
              <Section title="Completion Photos (post-work)" testid="section-completion-media-locked">
                <p className="text-sm text-slate-400">Save the inspection first to attach completion photos.</p>
              </Section>
            )}

            {/* Checked-by + Approved-by signatures */}
            <Section title="Work Completion Signatures" testid="section-completion-signatures">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3 bg-white/5 border border-white/10 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-teal-700">Checked by</h3>
                  <Field label="Name">
                    <input
                      value={form.checked_by_name}
                      onChange={(e) => set('checked_by_name', e.target.value)}
                      placeholder="Technician name"
                      className={inputCls}
                      data-testid="checked-by-name"
                    />
                  </Field>
                  <Field label="Signature">
                    <SignaturePad
                      value={form.checked_by_signature}
                      onChange={(d) => set('checked_by_signature', d)}
                      testid="checked-by-signature"
                    />
                  </Field>
                </div>
                <div className="space-y-3 bg-white/5 border border-white/10 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-teal-700">Approved by</h3>
                  <Field label="Name">
                    <input
                      value={form.approved_by_name}
                      onChange={(e) => set('approved_by_name', e.target.value)}
                      placeholder="Supervisor / Manager"
                      className={inputCls}
                      data-testid="approved-by-name"
                    />
                  </Field>
                  <Field label="Signature">
                    <SignaturePad
                      value={form.approved_by_signature}
                      onChange={(d) => set('approved_by_signature', d)}
                      testid="approved-by-signature"
                    />
                  </Field>
                </div>
              </div>
            </Section>

            {/* Customer approval + Executive signature */}
            <Section title="Approvals &amp; Signatures" testid="section-approval">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Customer */}
                <div className="space-y-3 bg-white/5 border border-white/10 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-teal-300">Customer Approval</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Customer printed name">
                      <input
                        value={form.customer_printed_name}
                        onChange={(e) => set('customer_printed_name', e.target.value)}
                        placeholder={form.customer_name || 'Full name as signed'}
                        className={inputCls}
                        data-testid="customer-printed-name"
                      />
                    </Field>
                    <Field label="Approval date">
                      <input type="date" value={form.customer_approval_date || ''} onChange={(e) => set('customer_approval_date', e.target.value)} className={inputCls} data-testid="approval-date" />
                    </Field>
                  </div>
                  <Field label="Customer signature">
                    <SignaturePad
                      value={form.customer_signature}
                      onChange={(d) => set('customer_signature', d)}
                      testid="customer-signature"
                    />
                  </Field>
                </div>

                {/* Executive */}
                <div className="space-y-3 bg-white/5 border border-white/10 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-teal-300">Executive &amp; Receiver</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Executive name / ID">
                      <input
                        value={form.executive_name_id}
                        onChange={(e) => set('executive_name_id', e.target.value)}
                        className={inputCls}
                        placeholder="E-007 / Anil"
                        data-testid="executive-2"
                      />
                    </Field>
                    <Field label="Receiver name">
                      <input
                        value={form.receiver_name}
                        onChange={(e) => set('receiver_name', e.target.value)}
                        className={inputCls}
                        placeholder="Person handing over the vehicle"
                        data-testid="receiver-name-2"
                      />
                    </Field>
                  </div>
                  <Field label="Executive signature">
                    <SignaturePad
                      value={form.executive_signature}
                      onChange={(d) => set('executive_signature', d)}
                      testid="executive-signature"
                    />
                  </Field>
                </div>
              </div>
            </Section>

            <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pb-10">
              <div className="text-xs text-slate-500">
                {form.customer_name?.trim() ? 'Ready to save.' : 'Customer name is required to save.'}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => save()}
                  disabled={saving || !form.customer_name?.trim()}
                  className="inline-flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition disabled:opacity-60"
                  data-testid="save-draft-btn"
                >
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  Save draft
                </button>
                <button
                  type="button"
                  onClick={() => save({ markCompleted: true })}
                  disabled={saving || !form.customer_name?.trim()}
                  className="inline-flex items-center gap-2 text-sm bg-teal-500 hover:bg-teal-400 disabled:bg-teal-700 disabled:opacity-60 text-slate-950 font-semibold px-4 py-2 rounded-lg transition"
                  data-testid="save-complete-btn"
                >
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
                  Save &amp; mark completed
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default InspectionForm;
